import React, { useEffect, useCallback, useState } from 'react';
import { Box } from '@mui/material';

const Timeline = ({ 
  canvasRef, 
  segments, 
  currentTime, 
  duration, 
  onTimelineClick,
  formatTimepoint
}) => {
  const [hoverTime, setHoverTime] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);

  // Calculate time from mouse position
  const calculateTimeFromPosition = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas || !duration) return null;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const progress = x / rect.width;
    return Math.max(0, Math.min(duration, progress * duration));
  }, [duration]);

  // Handle mouse move to update hover time and position
  const handleMouseMove = useCallback((event) => {
    const time = calculateTimeFromPosition(event);
    setHoverTime(time);
    
    // Get mouse position relative to the page for tooltip positioning
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      setMousePosition({
        x: event.clientX,
        y: rect.top - 35 // Position well above the timeline to avoid overlap
      });
      setShowTooltip(true);
    }
  }, [calculateTimeFromPosition]);

  // Handle mouse enter
  const handleMouseEnter = useCallback(() => {
    setShowTooltip(true);
  }, []);

  // Handle mouse leave to clear hover time
  const handleMouseLeave = useCallback(() => {
    setHoverTime(null);
    setShowTooltip(false);
  }, []);

  // Handle canvas resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeCanvas = () => {
      const container = canvas.parentElement;
      if (container) {
        const dpr = window.devicePixelRatio || 1;
        const cssWidth = container.clientWidth;
        const cssHeight = Math.max(8, container.clientHeight);

        // Adjust width & height for retina displays
        canvas.width = Math.max(1, Math.floor(cssWidth * dpr));
        canvas.height = Math.max(1, Math.floor(cssHeight * dpr));

        canvas.style.width = `${cssWidth}px`;
        canvas.style.height = `${cssHeight}px`;

        // Reset transform to account for the pixel ratio
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }
      }
    };

    const resizeObserver = new ResizeObserver(resizeCanvas);
    resizeObserver.observe(canvas.parentElement);

    resizeCanvas();

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          width: '100%',
          height: 8,
          cursor: 'pointer',
          backgroundColor: 'grey.100',
          borderRadius: 1,
          position: 'relative',
          overflow: 'hidden',
          border: 1,
          borderColor: 'grey.200',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            borderColor: 'primary.light',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            // Remove height change to prevent canvas resize during hover
            transform: 'scaleY(1.2)',
            transformOrigin: 'center'
          }
        }}
        onClick={onTimelineClick}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <canvas
          ref={canvasRef}
          width={800}
          height={8}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            transition: 'height 0.2s ease'
          }}
        />
      </Box>

      {/* Custom tooltip that follows mouse */}
      {showTooltip && hoverTime !== null && (
        <Box
          sx={{
            position: 'fixed',
            left: mousePosition.x,
            top: mousePosition.y,
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: 'medium',
            pointerEvents: 'none',
            zIndex: 1300,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
          }}
        >
          {formatTimepoint(hoverTime)}
        </Box>
      )}
    </Box>
  );
};

export default Timeline;
